// Copyright 1999-2026. WebPros International GmbH. All rights reserved.

import { useLazyQuery, useMutation } from '@apollo/client/react';
import { useLocalStorage } from 'common/hooks';
import { addToast } from 'helpers/toasts';
import { createContext, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { CopilotMessageFeedbackEnum } from 'schema';
import COPILOT_CONVERSATION_HISTORY from 'queries/CopilotConversationHistory.graphql';
import TOUCH_COPILOT_CONVERSATION from 'queries/TouchCopilotConversation.graphql';

const CURRENT_CONVERSATION_VARIABLES = { input: { archived: false, pagination: { page: 1, itemsPerPage: 1 } }, withMessages: true };

export type Answer = {
    role: 'assistant' | 'user';
    id: number;
    content?: string;
    error?: string;
    feedback?: CopilotMessageFeedbackEnum | null;
    chat_id?: string;
    message_id?: number;
    able_to_answer?: boolean;
    streaming?: boolean;
};

type CopilotContextType = {
    isRightSidebarOpen: boolean;
    setIsRightSidebarOpen: (value: boolean) => void;
    loading: boolean;
    answers: Answer[];
    setAnswers: (value: Answer[] | ((prev: Answer[]) => Answer[])) => void;
    chatId: string | undefined;
    setChatId: (value: string | undefined) => void;
    openConversation: (chatId: string) => Promise<void>;
    startNewChat: () => void;
};

export const CopilotContext = createContext<CopilotContextType>({
    isRightSidebarOpen: false,
    setIsRightSidebarOpen: () => { },
    loading: false,
    answers: [],
    setAnswers: () => { },
    chatId: undefined,
    setChatId: () => { },
    openConversation: () => Promise.resolve(),
    startNewChat: () => { },
});

type CopilotContextProviderProps = {
    children: React.ReactNode;
}

export const CopilotContextProvider = ({ children }: CopilotContextProviderProps) => {
    const [isRightSidebarOpen, setIsRightSidebarOpen] = useLocalStorage('isRightSidebarOpen', false);
    const [answers, setAnswers] = useState<Answer[]>([]);
    const [chatId, setChatId] = useState<string>();

    const [isNewChat, setIsNewChat] = useLocalStorage('isCopilotNewChat', false);
    const isNewChatRef = useRef(isNewChat);
    isNewChatRef.current = isNewChat;

    const [loadConversation, { loading, data, error: loadError }] = useLazyQuery(
        COPILOT_CONVERSATION_HISTORY,
        { fetchPolicy: 'network-only' },
    );
    const [touchConversation] = useMutation(TOUCH_COPILOT_CONVERSATION);

    const openConversation = useCallback(async (targetChatId: string) => {
        setIsRightSidebarOpen(true);
        await Promise.all([
            touchConversation({ variables: { input: { chatId: targetChatId } } }),
            loadConversation({ variables: { input: { externalId: targetChatId }, withMessages: true } }),
        ]);
    }, [loadConversation, touchConversation, setIsRightSidebarOpen]);

    useEffect(() => {
        const conversation = data?.viewer?.copilotConversationHistory?.nodes?.[0];
        if (!conversation?.messages) {
            return;
        }
        setChatId(conversation.externalId);
        setAnswers(
            conversation.messages.map(message => ({
                id: parseInt(message.id),
                role: message.role,
                content: message.content ?? undefined,
                feedback: message.feedback,
                chat_id: conversation.externalId,
                message_id: message.externalId,
            })),
        );
    }, [data]);

    useEffect(() => {
        if (loadError) {
            addToast({ intent: 'danger', message: String(loadError) });
        }
    }, [loadError]);

    useEffect(() => {
        if (chatId) {
            setIsNewChat(false);
        }
    }, [chatId, setIsNewChat]);

    useEffect(() => {
        if (isRightSidebarOpen && !isNewChatRef.current) {
            loadConversation({ variables: CURRENT_CONVERSATION_VARIABLES });
        }
    }, [isRightSidebarOpen, loadConversation]);

    const startNewChat = useCallback(() => {
        setAnswers([]);
        setChatId(undefined);
        setIsNewChat(true);
    }, [setIsNewChat]);

    const contextValue = useMemo(() => ({
        isRightSidebarOpen,
        setIsRightSidebarOpen,
        loading,
        answers,
        setAnswers,
        chatId,
        setChatId,
        openConversation,
        startNewChat,
    }), [
        isRightSidebarOpen, setIsRightSidebarOpen, loading, answers, setAnswers, chatId, openConversation, startNewChat,
    ]);

    return (
        <CopilotContext.Provider value={contextValue}>
            {children}
        </CopilotContext.Provider>
    );
};
